import {
  ArrowLeft,
  BookOpen,
  Code,
  FileText,
  FlaskConical,
  GitBranch,
  Link2,
  Loader2,
  Play,
  Save,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useParams } from '#router'
import { Button } from '@/components/ui/button'
import type { ProblemType } from '@/db/schema/enums'
import { usePageTitle } from '@/hooks/use-document-title'
import {
  PROBLEM_TAG_VOCABULARY,
  type ProblemTagSlug,
} from '@/lib/constants/problem-tags'
import { DEFAULT_TEST_TYPE } from '@/lib/constants/test-types'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import {
  toastError,
  toastSuccess,
  toastWarning,
} from '@/lib/utils/toast-helpers'
import { useSubtopics } from '../shared/hooks/use-subtopics'
import {
  addQuestionToCache,
  updateQuestionInCache,
} from './hooks/use-questions'
import { EditorialSection } from './problem-editor/editorial-section'
import { FollowUpsSection } from './problem-editor/followups-section'
import { MetadataSection } from './problem-editor/metadata-section'
import { RelationshipsSection } from './problem-editor/relationships-section'
import { splitSavedTestConfig } from './problem-editor/saved-test-config'
import { StatementSection } from './problem-editor/statement-section'
import type { TestConfiguration } from './problem-editor/tests-section'
import { TestsSection } from './problem-editor/tests-section'
import type {
  ApproachStrategy,
  EditorialBlock,
  ProblemFollowUp,
  ProblemSource,
  QuestionApproach,
} from './types'

/**
 * Ensures all approaches have corresponding editorial blocks.
 * Adds missing approach blocks at the end of the editorial.
 */
function ensureApproachesHaveEditorialBlocks(
  approaches: QuestionApproach[],
  editorialBlocks: EditorialBlock[],
): EditorialBlock[] {
  // Find approach IDs already referenced in editorial blocks
  const referencedApproachIds = new Set(
    editorialBlocks
      .filter(
        (b): b is { type: 'approach'; id: string; approachId: string } =>
          b.type === 'approach',
      )
      .map((b) => b.approachId),
  )

  // Find orphaned approaches (have an ID but no editorial block)
  const orphanedApproaches = approaches.filter(
    (a) => a.id && !referencedApproachIds.has(a.id),
  )

  // If no orphaned approaches, return as-is
  if (orphanedApproaches.length === 0) {
    return editorialBlocks
  }

  // Create editorial blocks for orphaned approaches
  const newBlocks: EditorialBlock[] = orphanedApproaches.map((approach) => ({
    type: 'approach' as const,
    id: crypto.randomUUID(),
    approachId: approach.id!,
  }))

  return [...editorialBlocks, ...newBlocks]
}

// Extended type for full problem data including new fields
export interface ProblemEditorData {
  id?: string
  externalId?: string | null
  name: string
  slug?: string
  statement?: string | null
  difficulty?: string | null
  premiumRequired?: boolean
  type?: ProblemType
  problemTagSlugs?: ProblemTagSlug[]
  acceptanceRate?: number | null
  freqBar?: number | null
  totalAccepted?: number | null
  totalSubmitted?: number | null
  companies?: string[]
  url?: string | null
  editorialUrl?: string | null
  source: string
  sourceId?: string | null // FK to Source table (UUID)
  sourceIcon?: string | null
  // Publishing & Access Control
  status?: 'draft' | 'published'
  visibility?: 'public' | 'client_only' | 'admin_only'
  hints?: {
    id?: string
    content: string
    order: number
  }[]
  constraints?: {
    id?: string
    content: string
    order: number
  }[]
  clarifications?: {
    id?: string
    question: string
    answer: string
    order: number
  }[]
  approaches?: (QuestionApproach & {
    hints?: { id?: string; content: string; order: number }[]
  })[]
  editorialFormat?: 'new' | 'legacy'
  editorialBlocks?: EditorialBlock[]
  sources?: ProblemSource[]
  testConfig?: TestConfiguration
  // Recipes for editorial recipe blocks (loaded from API)
  recipes?: {
    id: string
    name: string
    slug: string
    shortDescription: string | null
    descriptionDetails: string | null
    pseudocode: string | null
    implementations: { language: string; code: string }[]
    languageSummary: { python?: boolean; javascript?: boolean }
  }[]
}

type Section =
  | 'details'
  | 'statement'
  | 'tests'
  | 'editorial'
  | 'followups'
  | 'relationships'

const SECTIONS: {
  id: Section
  label: string
  icon: React.ComponentType<{ className?: string }>
}[] = [
  { id: 'details', label: 'Details', icon: FileText },
  { id: 'statement', label: 'Statement', icon: Code },
  { id: 'tests', label: 'Tests', icon: FlaskConical },
  { id: 'editorial', label: 'Editorial', icon: BookOpen },
  { id: 'followups', label: 'Follow-ups', icon: GitBranch },
  { id: 'relationships', label: 'Relationships', icon: Link2 },
]

function getDefaultProblemData(): ProblemEditorData {
  return {
    name: '',
    source: 'Beyond Cracking the Coding Interview',
    type: 'algorithms',
    problemTagSlugs: [],
    hints: [],
    constraints: [],
    clarifications: [],
    approaches: [],
    editorialBlocks: [],
    sources: [],
    testConfig: {
      testType: DEFAULT_TEST_TYPE,
      inputParameters: [],
      outputType: 'integer',
      testCases: [],
    },
    // Security: Default to most restrictive settings
    status: 'draft',
    visibility: 'admin_only',
  }
}

export function ProblemEditor() {
  const params = useParams()
  const location = useLocation()
  const { navigate } = location
  const saveTestsMutation = trpc.admin.questions.saveTests.useMutation()

  const isNew = params.id === 'new'
  const problemId = isNew ? null : params.id

  // Query params for linking to source content after creation
  const linkToSourceContent = location.searchParams.get('linkToSourceContent')
  const returnTo = location.searchParams.get('returnTo')

  const [activeSection, setActiveSection] = useState<Section>('details')
  const [saving, setSaving] = useState(false)
  // The tests section saves the test config on its own; Save Changes writes
  // it too, so each side waits for the other. Owned here rather than by the
  // section, which unmounts on a sidebar switch while its save is in flight.
  const [testConfigSaving, setTestConfigSaving] = useState(false)
  // Languages whose authored starter-code stub the last test-config save kept.
  // Keyed by problem: the editor stays mounted across problems, and one
  // problem's kept-stub note must not follow the admin to the next.
  const [preservedStubs, setPreservedStubs] = useState<{
    problemId: string
    languages: string[]
  } | null>(null)
  // Dropping it on the way out matters as much as keying it: hiding a
  // foreign problem's note still leaves it to reappear on the way back,
  // without a save. Cleared only when it belongs to another problem, so the
  // redirect that follows creating one keeps the note its own save just set.
  useEffect(() => {
    setPreservedStubs((current) =>
      current && current.problemId !== problemId ? null : current,
    )
  }, [problemId])
  const [formData, setFormData] = useState<ProblemEditorData>(() =>
    getDefaultProblemData(),
  )
  const [followUps, setFollowUps] = useState<ProblemFollowUp[]>([])
  const saveFollowUpsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  // ── tRPC Queries ──────────────────────────────────────────────────────────

  // Load problem data (getById)
  const problemQuery = trpc.admin.questions.getById.useQuery(
    { id: problemId! },
    { enabled: !!problemId },
  )

  // Load test configuration
  const testsQuery = trpc.admin.questions.getTests.useQuery(
    { problemId: problemId! },
    { enabled: !!problemId },
  )

  // Load follow-ups for this problem
  const followUpsQuery = trpc.admin.questions.getFollowUps.useQuery(
    { problemId: problemId! },
    { enabled: !!problemId },
  )

  // ── tRPC Mutations ────────────────────────────────────────────────────────

  const createQuestionMutation = trpc.admin.questions.create.useMutation()
  const updateQuestionMutation = trpc.admin.questions.update.useMutation()
  const createFollowUpMutation = trpc.admin.followUps.create.useMutation()
  const updateFollowUpMutation = trpc.admin.followUps.update.useMutation()
  const deleteFollowUpMutation = trpc.admin.followUps.delete.useMutation()
  const createSourceContentItemMutation =
    trpc.admin.content.createSourceContentItem.useMutation()

  // ── Derived loading / error state ─────────────────────────────────────────

  const loading = !isNew && (problemQuery.isLoading || testsQuery.isLoading)
  const error = !isNew && problemQuery.error ? problemQuery.error.message : null

  // Update page title with problem name
  usePageTitle(isNew ? 'New Problem' : formData.name || null)

  // Fetch subtopics (grouped by topic in the picker) for approach tagging
  const { subtopics } = useSubtopics()
  const allStrategies: ApproachStrategy[] = useMemo(() => {
    return subtopics.map((s) => ({
      id: s.id,
      name: s.name,
      slug: s.slug,
      topicName: s.topicName,
    }))
  }, [subtopics])

  // ── Populate formData from query results ──────────────────────────────────

  // When problem data arrives, populate formData
  useEffect(() => {
    if (!problemQuery.data) return

    const data = problemQuery.data

    // Determine test config from the tests query
    // readTestConfiguration returns the fully transformed shape matching TestConfiguration
    const testConfig: TestConfiguration = testsQuery.data
      ? (testsQuery.data as TestConfiguration)
      : {
          testType: DEFAULT_TEST_TYPE,
          inputParameters: [],
          outputType: 'integer',
          testCases: [],
        }

    // Map approaches: convert solutionType null → undefined for frontend type compat
    const approaches = (data.approaches || []).map((a) => ({
      ...a,
      solutionType: a.solutionType ?? undefined,
    }))
    const editorialBlocks = ensureApproachesHaveEditorialBlocks(
      approaches,
      (data.editorialBlocks || []) as EditorialBlock[],
    )

    setFormData({
      id: data.id,
      externalId: data.externalId,
      name: data.name,
      slug: data.slug ?? undefined,
      statement: data.statement,
      difficulty: data.difficulty?.name ?? null,
      premiumRequired: data.premiumRequired,
      type: data.type,
      // TagDefinition is grain-agnostic (a future CodeReviewTag junction can
      // reuse it), so validate joined slugs against the problem-tag vocabulary
      // rather than asserting: an off-vocab slug would break the multiselect
      // match and get rejected by the router's z.enum on the next save.
      problemTagSlugs: (data.problemTags || [])
        .map((t) => t.slug)
        .filter((slug): slug is ProblemTagSlug =>
          PROBLEM_TAG_VOCABULARY.some((t) => t.slug === slug),
        ),
      acceptanceRate: data.acceptanceRate,
      freqBar: null, // Not returned by getById
      totalAccepted: data.totalAccepted,
      totalSubmitted: data.totalSubmitted,
      companies: [], // Not returned by getById
      url: data.url,
      editorialUrl: data.editorialUrl,
      source: data.source,
      sourceIcon: data.sourceIcon,
      // Editorial format
      editorialFormat: data.editorialFormat || 'new',
      // Publishing & Access Control (fallback to most restrictive)
      status: data.status || 'draft',
      visibility: data.visibility || 'admin_only',
      hints: data.hints || [],
      constraints: data.constraints || [],
      clarifications: data.clarifications || [],
      approaches,
      editorialBlocks,
      sources: data.sources || [],
      testConfig: testConfig || {
        testType: DEFAULT_TEST_TYPE,
        inputParameters: [],
        outputType: 'integer',
        testCases: [],
      },
      recipes: data.recipes || [],
    })
  }, [problemQuery.data, testsQuery.data])

  // Populate follow-ups from query
  useEffect(() => {
    if (!followUpsQuery.data) return

    const data = followUpsQuery.data
    // getFollowUps returns { followUps, approaches }
    // Map approaches array to approachIds for the ProblemFollowUp type
    setFollowUps(
      (data.followUps || []).map((f) => ({
        id: f.id,
        prompt: f.prompt,
        order: f.order,
        expectedTimeComplexity: f.expectedTimeComplexity,
        expectedSpaceComplexity: f.expectedSpaceComplexity,
        prereqs: f.prereqs || [],
        approachIds: f.approaches || [],
      })),
    )
  }, [followUpsQuery.data])

  // Save a single follow-up (create or update)
  // Note: Big O formatting is now handled by the auto-save components
  const saveFollowUp = useCallback(
    async (followUp: ProblemFollowUp): Promise<string | null> => {
      if (!problemId) return null

      // Don't save follow-ups without a prompt (required by API)
      if (!followUp.prompt || followUp.prompt.trim() === '') {
        return null
      }

      try {
        if (followUp.id) {
          // Update existing
          await updateFollowUpMutation.mutateAsync({
            id: followUp.id,
            prompt: followUp.prompt,
            order: followUp.order,
            expectedTimeComplexity: followUp.expectedTimeComplexity,
            expectedSpaceComplexity: followUp.expectedSpaceComplexity,
            prereqs: followUp.prereqs,
            approachIds: followUp.approachIds,
          })
          return followUp.id
        } else {
          // Create new
          const result = await createFollowUpMutation.mutateAsync({
            problemId,
            prompt: followUp.prompt,
            order: followUp.order,
            expectedTimeComplexity: followUp.expectedTimeComplexity,
            expectedSpaceComplexity: followUp.expectedSpaceComplexity,
            prereqs: followUp.prereqs,
            approachIds: followUp.approachIds,
          })
          return result.followUp.id
        }
      } catch (err) {
        console.error('Error saving follow-up:', err)
      }
      return null
    },
    [problemId, updateFollowUpMutation, createFollowUpMutation],
  )

  // Delete a follow-up
  const deleteFollowUp = useCallback(
    async (followUpId: string) => {
      try {
        await deleteFollowUpMutation.mutateAsync({ id: followUpId })
      } catch (err) {
        console.error('Error deleting follow-up:', err)
      }
    },
    [deleteFollowUpMutation],
  )

  // Track pending saves using tempId or id as key
  const pendingSavesRef = useRef<Map<string, ProblemFollowUp>>(new Map())

  // Handle follow-ups update with debounced save
  const handleFollowUpsUpdate = useCallback(
    (newFollowUps: ProblemFollowUp[]) => {
      // Find deleted follow-ups (only those with real IDs)
      const deletedFollowUps = followUps.filter(
        (old) => old.id && !newFollowUps.find((n) => n.id === old.id),
      )

      // Delete removed follow-ups
      for (const deleted of deletedFollowUps) {
        if (deleted.id) {
          deleteFollowUp(deleted.id)
        }
      }

      // Find which follow-ups changed and need saving
      for (const newFollowUp of newFollowUps) {
        // Skip if no content (empty prompt)
        if (!newFollowUp.prompt || newFollowUp.prompt.trim() === '') continue

        // Get tracking key: use real id if exists, otherwise tempId
        const trackingKey = newFollowUp.id || newFollowUp.tempId
        if (!trackingKey) continue

        // For new follow-ups (no real id), always queue for save
        if (!newFollowUp.id) {
          pendingSavesRef.current.set(trackingKey, newFollowUp)
          continue
        }

        // For existing follow-ups, check if changed
        const oldFollowUp = followUps.find((f) => f.id === newFollowUp.id)
        if (!oldFollowUp) {
          pendingSavesRef.current.set(trackingKey, newFollowUp)
          continue
        }

        // Check if anything changed
        const changed =
          oldFollowUp.prompt !== newFollowUp.prompt ||
          oldFollowUp.order !== newFollowUp.order ||
          oldFollowUp.expectedTimeComplexity !==
            newFollowUp.expectedTimeComplexity ||
          oldFollowUp.expectedSpaceComplexity !==
            newFollowUp.expectedSpaceComplexity ||
          JSON.stringify([...oldFollowUp.prereqs].sort()) !==
            JSON.stringify([...newFollowUp.prereqs].sort()) ||
          JSON.stringify([...oldFollowUp.approachIds].sort()) !==
            JSON.stringify([...newFollowUp.approachIds].sort())

        if (changed) {
          pendingSavesRef.current.set(trackingKey, newFollowUp)
        }
      }

      setFollowUps(newFollowUps)

      // Debounce saving
      if (pendingSavesRef.current.size > 0) {
        if (saveFollowUpsTimeoutRef.current) {
          clearTimeout(saveFollowUpsTimeoutRef.current)
        }

        saveFollowUpsTimeoutRef.current = setTimeout(async () => {
          const followUpsToSave = Array.from(pendingSavesRef.current.values())
          pendingSavesRef.current.clear()

          // Save follow-ups and collect tempId -> realId mappings
          const tempIdToRealId = new Map<string, string>()

          for (const followUp of followUpsToSave) {
            const newId = await saveFollowUp(followUp)
            if (newId && !followUp.id && followUp.tempId) {
              tempIdToRealId.set(followUp.tempId, newId)
            }
          }

          // Update state: replace tempId with real id
          if (tempIdToRealId.size > 0) {
            setFollowUps((prev) =>
              prev.map((f) => {
                if (f.id) return f // Already has real ID
                if (!f.tempId) return f // No tempId to match

                const realId = tempIdToRealId.get(f.tempId)
                if (realId) {
                  // Replace tempId with real id, clear tempId
                  return { ...f, id: realId, tempId: undefined }
                }
                return f
              }),
            )
          }
        }, 500)
      }
    },
    [followUps, saveFollowUp, deleteFollowUp],
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveFollowUpsTimeoutRef.current) {
        clearTimeout(saveFollowUpsTimeoutRef.current)
      }
    }
  }, [])

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toastError(null, 'Problem name is required')
      return
    }
    if (!formData.source) {
      toastError(null, 'Source is required')
      return
    }

    setSaving(true)
    try {
      let savedData: { id: string; [key: string]: unknown }

      if (isNew) {
        // Create new problem
        const result = await createQuestionMutation.mutateAsync({
          name: formData.name,
          externalId: formData.externalId || undefined,
          statement: formData.statement || undefined,
          difficulty: formData.difficulty || undefined,
          premiumRequired: formData.premiumRequired,
          type: formData.type,
          problemTagSlugs: formData.problemTagSlugs,
          acceptanceRate: formData.acceptanceRate ?? undefined,
          totalAccepted: formData.totalAccepted ?? undefined,
          totalSubmitted: formData.totalSubmitted ?? undefined,
          companies: formData.companies,
          url: formData.url || undefined,
          editorialUrl: formData.editorialUrl || undefined,
          sourceId: formData.sourceId || undefined,
          hints: formData.hints?.map((h) => ({ content: h.content })),
          constraints: formData.constraints?.map((c) => ({
            content: c.content,
          })),
          clarifications: formData.clarifications?.map((c) => ({
            question: c.question,
            answer: c.answer,
          })),
          approaches: formData.approaches,
        })
        savedData = result as { id: string; [key: string]: unknown }
      } else {
        // Update existing problem
        await updateQuestionMutation.mutateAsync({
          id: problemId!,
          name: formData.name,
          externalId: formData.externalId,
          statement: formData.statement,
          difficulty: formData.difficulty,
          premiumRequired: formData.premiumRequired,
          type: formData.type,
          problemTagSlugs: formData.problemTagSlugs,
          acceptanceRate: formData.acceptanceRate,
          totalAccepted: formData.totalAccepted,
          totalSubmitted: formData.totalSubmitted,
          url: formData.url,
          editorialUrl: formData.editorialUrl,
          sourceId: formData.sourceId || undefined,
          hints: formData.hints?.map((h) => ({ content: h.content })),
          constraints: formData.constraints?.map((c) => ({
            content: c.content,
          })),
          clarifications: formData.clarifications?.map((c) => ({
            question: c.question,
            answer: c.answer,
          })),
          approaches: formData.approaches,
          editorialBlocks: formData.editorialBlocks,
          editorialFormat: formData.editorialFormat,
          status: formData.status,
          visibility: formData.visibility,
        })
        savedData = { id: problemId! }
      }

      // For new problems, immediately add to cache and prepare to redirect
      // This ensures the problem is visible even if subsequent saves (test config) fail
      if (isNew) {
        addQuestionToCache(savedData as any)
      }

      // Track if test config save fails (we'll show a warning but still redirect)
      let testConfigError: string | null = null

      // Also save test configuration if we have one
      const targetProblemId = isNew ? savedData.id : problemId
      if (formData.testConfig && targetProblemId) {
        try {
          const savedTestConfig = await saveTestsMutation.mutateAsync({
            problemId: targetProblemId,
            testConfig: formData.testConfig as unknown as Record<
              string,
              unknown
            >,
          })
          const split = splitSavedTestConfig(savedTestConfig)
          savedData.testConfig = split.testConfig
          setPreservedStubs({
            problemId: targetProblemId,
            languages: split.preservedStarterCode,
          })
        } catch (err) {
          testConfigError =
            err instanceof Error
              ? err.message
              : 'Failed to save test configuration'
        }
      }

      if (isNew) {
        // If linking to source content, create the link and redirect back
        if (linkToSourceContent) {
          let linkSucceeded = false
          try {
            const linkRes = await createSourceContentItemMutation.mutateAsync({
              sourceContentId: linkToSourceContent,
              contentType: 'PROBLEM',
              contentId: savedData.id,
              order: 0, // API will handle ordering
            })

            if (linkRes) {
              linkSucceeded = true
              if (testConfigError) {
                toastWarning(
                  `Problem created and linked, but test config failed: ${testConfigError}`,
                )
              } else {
                toastSuccess('Problem created and linked')
              }
            }
          } catch (err) {
            console.error('Error linking problem to source content:', err)
            toastError(
              null,
              'Problem created but failed to link to source content',
            )
          }

          // Only redirect back to source content if the link was successfully created
          // Otherwise, redirect to the problem edit page so the user can see their problem
          if (linkSucceeded && returnTo) {
            navigate(returnTo)
            return
          }
        } else if (testConfigError) {
          // Show warning but still redirect since problem was created
          toastWarning(
            `Problem created, but test config failed: ${testConfigError}`,
          )
        }

        // Redirect to the edit page after creation
        navigate(`/admin/problems/${savedData.id}`)
      } else {
        // Update the cache with the saved data
        updateQuestionInCache(savedData as any)
        setFormData((prev) => ({ ...prev, ...savedData }))
        if (testConfigError) {
          toastWarning(
            `Problem saved, but test config failed: ${testConfigError}`,
          )
        } else {
          toastSuccess('Problem saved successfully')
        }
      }
    } catch (err) {
      toastError(err, 'Failed to save problem')
    } finally {
      setSaving(false)
    }
  }

  const updateFormData = useCallback((updates: Partial<ProblemEditorData>) => {
    setFormData((prev) => ({ ...prev, ...updates }))
  }, [])

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    )
  }

  if (error && !isNew) {
    return (
      <div className="container mx-auto max-w-6xl px-4 py-8">
        <div className="bg-destructive/10 border-destructive/30 text-destructive rounded-lg border p-4">
          {error}
        </div>
        <Button asChild className="mt-4" variant="outline">
          <Link href="/admin/problems">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Problems
          </Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="container mx-auto max-w-7xl px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="sm">
            <Link href="/admin/problems">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isNew ? 'Create Problem' : formData.name || 'Edit Problem'}
            </h1>
            {formData.slug && (
              <div className="flex items-center gap-2">
                <p className="text-muted-foreground font-mono text-sm">
                  {formData.slug}
                </p>
                <Button
                  asChild
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  title="Preview in editor"
                >
                  <Link href={`/editor?problem=${formData.slug}`}>
                    <Play className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving || testConfigSaving}>
          {saving || testConfigSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              {isNew ? 'Create' : 'Save Changes'}
            </>
          )}
        </Button>
      </div>

      {/* Main content with sidebar */}
      <div className="flex gap-6">
        {/* Sidebar */}
        <nav className="w-48 flex-shrink-0">
          <ul className="space-y-1">
            {SECTIONS.map((section) => {
              const Icon = section.icon
              return (
                <li key={section.id}>
                  <button
                    type="button"
                    onClick={() => setActiveSection(section.id)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      activeSection === section.id
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {section.label}
                  </button>
                </li>
              )
            })}
          </ul>
        </nav>

        {/* Content area */}
        <div className="bg-card border-border flex-1 rounded-lg border p-6">
          {activeSection === 'details' && (
            <MetadataSection formData={formData} onUpdate={updateFormData} />
          )}
          {activeSection === 'statement' && (
            <StatementSection
              statement={formData.statement || ''}
              onUpdateStatement={(statement) => updateFormData({ statement })}
              title={formData.name}
              testConfig={
                formData.testConfig || {
                  testType: DEFAULT_TEST_TYPE,
                  inputParameters: [],
                  outputType: 'integer',
                  testCases: [],
                }
              }
              constraints={formData.constraints || []}
              onUpdateConstraints={(constraints) =>
                updateFormData({ constraints })
              }
              sources={formData.sources || []}
              clarifications={formData.clarifications || []}
              onUpdateClarifications={(clarifications) =>
                updateFormData({ clarifications })
              }
              hints={formData.hints || []}
              onUpdateHints={(hints) => updateFormData({ hints })}
            />
          )}
          {activeSection === 'tests' && (
            <TestsSection
              testConfig={
                formData.testConfig || {
                  testType: DEFAULT_TEST_TYPE,
                  inputParameters: [],
                  outputType: 'integer',
                  testCases: [],
                }
              }
              onUpdate={(testConfig) => updateFormData({ testConfig })}
              problemId={problemId ?? undefined}
              problemSlug={formData.slug}
              approaches={formData.approaches}
              preservedStarterCode={
                preservedStubs && preservedStubs.problemId === problemId
                  ? preservedStubs.languages
                  : undefined
              }
              savePending={saving || testConfigSaving}
              onSave={async (testConfig, options) => {
                if (!problemId) return

                setTestConfigSaving(true)
                try {
                  const savedConfig = await saveTestsMutation.mutateAsync({
                    problemId,
                    testConfig: testConfig as unknown as Record<
                      string,
                      unknown
                    >,
                    regenerateStarterCode: options?.regenerateStarterCode,
                  })

                  // Update formData with the response (which includes IDs for new test cases)
                  const split = splitSavedTestConfig(savedConfig)
                  updateFormData({ testConfig: split.testConfig })
                  setPreservedStubs({
                    problemId,
                    languages: split.preservedStarterCode,
                  })
                } finally {
                  setTestConfigSaving(false)
                }
              }}
            />
          )}
          {activeSection === 'editorial' && (
            <EditorialSection
              editorialFormat={formData.editorialFormat || 'new'}
              onUpdateEditorialFormat={(editorialFormat) =>
                updateFormData({ editorialFormat })
              }
              editorialBlocks={formData.editorialBlocks || []}
              approaches={formData.approaches || []}
              onUpdateBlocks={(editorialBlocks) =>
                updateFormData({ editorialBlocks })
              }
              onUpdateApproaches={(approaches) =>
                updateFormData({ approaches })
              }
              allStrategies={allStrategies}
              allRecipes={(formData.recipes || []).map((r) => ({
                id: r.id,
                name: r.name,
                slug: r.slug,
                shortDescription: r.shortDescription,
                languageSummary: r.languageSummary,
              }))}
              recipesForPreview={(formData.recipes || []).map((r) => ({
                id: r.id,
                name: r.name,
                shortDescription: r.shortDescription,
                descriptionDetails: r.descriptionDetails,
                pseudocode: r.pseudocode,
                implementations: r.implementations,
              }))}
              problemSlug={formData.slug}
              functionNameOverride={formData.testConfig?.functionNames?.[0]}
            />
          )}
          {activeSection === 'followups' && (
            <FollowUpsSection
              followUps={followUps}
              approaches={formData.approaches || []}
              onUpdate={handleFollowUpsUpdate}
              onSaveFollowUp={saveFollowUp}
            />
          )}
          {activeSection === 'relationships' && (
            <RelationshipsSection formData={formData} />
          )}
        </div>
      </div>
    </div>
  )
}
